/**
 * Coordinate parsing and detection utilities.
 * Includes loose matching for common coordinate formats (DD, DMS, DDM)
 * and parsing into Mapbox-friendly [longitude, latitude] pairs.
 */

import { type LngLatBase } from '@/views/map/types';
import { type BoundingBoxString } from '@/features/types';
import { WKTBBoxToArray } from '@/helpers/stringConversion';
import { captureException } from '@sentry/react';

// Regex pattern for detecting coordinates in Decimal Degrees (DD) format.
// Matches Decimal Degrees (DD) format:
// Examples: "40.446 -79.982", "-23.5505, -46.6333"
// Supports optional +/- sign, comma or space separator.
const decimalDegreesRegex =
  /^([+-]?\d{1,3}(?:\.\d+)?)[,\s]+([+-]?\d{1,3}(?:\.\d+)?)$/;

// Regex pattern for detecting coordinates in Degrees Minutes Seconds (DMS) format.
// Matches Degrees Minutes Seconds (DMS) format:
// Examples: "40° 26' 46" N 79° 58' 56" W"
// Supports optional degree (°), minute ('), second (") symbols and cardinal directions (N, S, E, W).
const dmsRegex =
  /^(\d{1,3})[°\s]+(\d{1,2})['\s]+(\d{1,2}(?:\.\d+)?)["\s]*([NnSs])?\s*(\d{1,3})[°\s]+(\d{1,2})['\s]+(\d{1,2}(?:\.\d+)?)["\s]*([EeWw])?$/;

// Regex pattern for detecting coordinates in Degrees Decimal Minutes (DDM) format.
// Matches Degrees Decimal Minutes (DDM) format:
// Examples: "40° 26.767' N 79° 58.933' W"
// Supports optional degree (°), minute (') symbols and cardinal directions (N, S, E, W).
const ddmRegex =
  /^(\d{1,3})[°\s]+(\d{1,2}(?:\.\d+)?)['\s]*([NnSs])?\s*(\d{1,3})[°\s]+(\d{1,2}(?:\.\d+)?)['\s]*([EeWw])?$/;

/**
 * Validates that coordinates are within valid geographic bounds.
 * Latitude must be between -90 and 90 degrees.
 * Longitude must be between -180 and 180 degrees.
 */
function isValidCoordinate(lng: number, lat: number): boolean {
  return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
}

/**
 * Attempts to parse a coordinate string into a [longitude, latitude] pair.
 * Supports Decimal Degrees (DD), Degrees Minutes Seconds (DMS),
 * and Degrees Decimal Minutes (DDM).
 * Returns null if the input doesn't match any known format or if coordinates are out of bounds.
 */
export function parseCoordinatePair(
  input: string | null | undefined
): LngLatBase | null {
  try {
    if (typeof input !== 'string') return null;

    const cleaned = input.trim().replace(/[()]/g, '');

    // Decimal Degrees: e.g. "40.446 -79.982" or "-23.5505, -46.6333"
    const decimalMatch = decimalDegreesRegex.exec(cleaned);
    if (decimalMatch) {
      const lat = parseFloat(decimalMatch[1]);
      const lon = parseFloat(decimalMatch[2]);
      if (isValidCoordinate(lon, lat)) {
        return { lng: lon, lat: lat }; // Mapbox format is {lng, lat}
      }
      return null;
    }

    // DMS Parsing: e.g. "40° 26' 46" N 79° 58' 56" W"
    const dmsMatch = dmsRegex.exec(cleaned);
    if (dmsMatch) {
      const lat = dmsToDecimal(
        Number(dmsMatch[1]),
        Number(dmsMatch[2]),
        Number(dmsMatch[3]),
        dmsMatch[4]
      );
      const lon = dmsToDecimal(
        Number(dmsMatch[5]),
        Number(dmsMatch[6]),
        Number(dmsMatch[7]),
        dmsMatch[8]
      );
      if (isValidCoordinate(lon, lat)) {
        return { lng: lon, lat: lat };
      }
      return null;
    }

    // DDM Parsing: e.g. "40° 26.767' N 79° 58.933' W"
    const ddmMatch = ddmRegex.exec(cleaned);
    if (ddmMatch) {
      const lat = ddmToDecimal(
        Number(ddmMatch[1]),
        Number(ddmMatch[2]),
        ddmMatch[3]
      );
      const lon = ddmToDecimal(
        Number(ddmMatch[4]),
        Number(ddmMatch[5]),
        ddmMatch[6]
      );
      if (isValidCoordinate(lon, lat)) {
        return { lng: lon, lat: lat };
      }
      return null;
    }

    return null; // Didn't match any known formats
  } catch (e) {
    // Just return null on any parsing errors
    captureException(e, { extra: { input } });
    return null;
  }
}

/**
 * Converts Degrees Minutes Seconds (DMS) components to decimal degrees.
 * Handles directional indicators (N, S, E, W).
 */
function dmsToDecimal(
  degrees: number,
  minutes: number,
  seconds: number,
  direction?: string
): number {
  let decimal = degrees + minutes / 60 + seconds / 3600;
  if (direction && /[SsWw]/.test(direction)) {
    decimal *= -1;
  }
  return decimal;
}

/**
 * Converts Degrees Decimal Minutes (DDM) components to decimal degrees.
 * Handles directional indicators (N, S, E, W).
 */
function ddmToDecimal(
  degrees: number,
  minutes: number,
  direction?: string
): number {
  let decimal = degrees + minutes / 60;
  if (direction && /[SsWw]/.test(direction)) {
    decimal *= -1;
  }
  return decimal;
}

/**
 * Computes the center point of a WKT bounding box string.
 * @param bbox - WKT bounding box string
 * @returns Center point as {lng, lat}, or null if bbox is invalid
 */
export function getBboxCenter(bbox?: BoundingBoxString): LngLatBase | null {
  const coords = WKTBBoxToArray(bbox);
  if (coords.length !== 4) return null;
  const [minLng, minLat, maxLng, maxLat] = coords;
  return {
    lng: (minLng + maxLng) / 2,
    lat: (minLat + maxLat) / 2,
  };
}
